import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toLongDate } from '@utils/Dated';
import { SnackbarApiErrorMessage, useLocalization } from '@ui-framework/index';
import { IconLibrary } from '@product-framework/index';
import { getCurrentUserId } from '../utils';
import { addIssueComment, getIssueComments, updateIssueComment } from './api';

const normalizeId = value => value?.toLowerCase?.() || '';
const COMMENTS_PAGE_SIZE = 5;

const getCommentTime = comment =>
  comment?.createdDateTime
    ? new Date(comment.createdDateTime).getTime() || 0
    : 0;

const sortNewestFirst = comments =>
  [...comments].sort(
    (first, second) => getCommentTime(second) - getCommentTime(first),
  );

const getCommentEditedTime = comment => {
  if ((comment?.recordVersion ?? 1) <= 1 || !comment?.lastModifiedDateTime) {
    return null;
  }
  const createdTime = getCommentTime(comment);
  const modifiedTime = new Date(comment.lastModifiedDateTime).getTime() || 0;

  return modifiedTime > createdTime ? comment.lastModifiedDateTime : null;
};

const getAuthorDisplayName = ({ comment, fallback }) =>
  comment.author?.displayName || fallback;

const getAuthorCompanyContext = comment =>
  comment.author?.companyDisplayName ||
  comment.author?.companyName ||
  comment.author?.company?.displayName ||
  comment.author?.company?.name ||
  '';

function IssueCommentCard({
  comment,
  currentUserId,
  canManageComments,
  editingId,
  editingBody,
  saving,
  setEditingId,
  setEditingBody,
  saveEdit,
  formatMessage,
}) {
  const isOwnComment =
    currentUserId &&
    normalizeId(comment.author?.userId) &&
    currentUserId === normalizeId(comment.author?.userId);
  const isEditing = editingId === comment.id;
  const editedTime = getCommentEditedTime(comment);
  const unknownUserLabel = formatMessage({
    id: 'UNKNOWN_USER',
    defaultMessage: 'Unknown user',
  });
  // Always show the author's real display name so the thread reads
  // consistently; own comments are distinguished visually instead.
  const authorName = getAuthorDisplayName({
    comment,
    fallback: unknownUserLabel,
  });
  const companyContext = getAuthorCompanyContext(comment);
  const messageDate = comment.createdDateTime
    ? toLongDate(comment.createdDateTime)
    : '-';
  const CommentAvatarIcon = IconLibrary.User;

  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent={isOwnComment ? 'flex-end' : 'flex-start'}
      alignItems="flex-start"
      sx={{ width: '100%' }}>
      {!isOwnComment && (
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, mt: 1 }}>
          <CommentAvatarIcon fontSize="small" />
        </Avatar>
      )}
      <Card
        variant="outlined"
        sx={{
          bgcolor: isOwnComment ? 'action.hover' : 'background.paper',
          borderRadius: 1,
          borderRight: isOwnComment ? 3 : undefined,
          borderRightColor: isOwnComment ? 'primary.main' : undefined,
          display: 'inline-block',
          maxWidth: { xs: 'calc(100% - 40px)', md: '78%' },
          minWidth: { xs: 0, md: '45%' },
          overflow: 'visible',
          p: 2,
          position: 'relative',
        }}>
        <Chip
          size="small"
          color="primary"
          label={formatMessage({
            id: 'COMMENT',
            defaultMessage: 'COMMENT',
          })}
          sx={{
            borderRadius: 0.5,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            height: 20,
            left: 12,
            position: 'absolute',
            textTransform: 'uppercase',
            top: -10,
          }}
        />
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={0.5}
            useFlexGap
            sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="span"
                variant="caption"
                color="primary.main"
                sx={{ fontWeight: 'bold' }}>
                {authorName}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}>
                {messageDate}
                {editedTime
                  ? ` · ${formatMessage({
                      id: 'EDITED',
                      defaultMessage: 'edited',
                    })}`
                  : ''}
              </Typography>
              {companyContext && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block">
                  {companyContext}
                </Typography>
              )}
            </Box>
            {canManageComments && isOwnComment && !isEditing && (
              <Button
                size="small"
                onClick={() => {
                  setEditingId(comment.id);
                  setEditingBody(comment.body || '');
                }}>
                {formatMessage({
                  id: 'EDIT',
                  defaultMessage: 'Edit',
                })}
              </Button>
            )}
          </Stack>
          {isEditing ? (
            <Stack spacing={1}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={editingBody}
                onChange={event => setEditingBody(event.target.value)}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving || !editingBody.trim()}
                  onClick={() => saveEdit(comment)}>
                  {formatMessage({
                    id: 'SAVE',
                    defaultMessage: 'Save',
                  })}
                </Button>
                <Button
                  size="small"
                  disabled={saving}
                  onClick={() => {
                    setEditingId(null);
                    setEditingBody('');
                  }}>
                  {formatMessage({
                    id: 'CANCEL',
                    defaultMessage: 'Cancel',
                  })}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
              {comment.body}
            </Typography>
          )}
        </Stack>
      </Card>
      {isOwnComment && (
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, mt: 1 }}>
          <CommentAvatarIcon fontSize="small" />
        </Avatar>
      )}
    </Stack>
  );
}

function IssueComments({
  issueId,
  dataProvider,
  userAccess,
  canManageComments = false,
  onChanged = () => undefined,
}) {
  const [comments, setComments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { formatMessage } = useLocalization();
  const currentUserId = normalizeId(getCurrentUserId(userAccess));

  const loadComments = async () => {
    if (!issueId) return;
    setLoading(true);
    const result = await getIssueComments({
      dataProvider,
      issueId,
      page,
      limit: COMMENTS_PAGE_SIZE,
    });
    setLoading(false);
    if (result.ok) {
      const responseData = result.data;
      const responseComments = Array.isArray(responseData)
        ? responseData
        : responseData?.data || [];
      const sortedComments = sortNewestFirst(responseComments);
      const backendTotal = responseData?.total ?? responseData?.totalCount;
      const pageCount =
        responseData?.totalPages ||
        Math.ceil(
          (backendTotal ?? sortedComments.length) /
            (responseData?.limit || COMMENTS_PAGE_SIZE),
        );
      const shouldClientPaginate = backendTotal === undefined;

      setTotalPages(pageCount);
      setComments(
        shouldClientPaginate && sortedComments.length > COMMENTS_PAGE_SIZE
          ? sortedComments.slice(
              (page - 1) * COMMENTS_PAGE_SIZE,
              page * COMMENTS_PAGE_SIZE,
            )
          : sortedComments,
      );
      setError(null);
      return;
    }
    setError(
      result?.errors || [
        {
          message: formatMessage({
            id: 'COULD_NOT_LOAD_COMMENTS',
            defaultMessage: 'Could not load comments',
          }),
        },
      ],
    );
  };

  useEffect(() => {
    loadComments();
  }, [issueId, page]);

  useEffect(() => {
    setPage(1);
  }, [issueId]);

  const submitComment = async () => {
    const trimmedBody = body.trim();
    if (!trimmedBody) {
      setError([
        {
          message: formatMessage({
            id: 'COMMENT_IS_REQUIRED',
            defaultMessage: 'Comment is required',
          }),
        },
      ]);
      return;
    }
    setSaving(true);
    const result = await addIssueComment({
      dataProvider,
      issueId,
      body: trimmedBody,
    });
    setSaving(false);
    if (result.ok) {
      setBody('');
      setComposerOpen(false);
      if (page === 1) {
        await loadComments();
      } else {
        setPage(1);
      }
      onChanged();
      return;
    }
    setError(
      result?.errors || [
        {
          message: formatMessage({
            id: 'COULD_NOT_ADD_COMMENT',
            defaultMessage: 'Could not add comment',
          }),
        },
      ],
    );
  };

  const saveEdit = async comment => {
    const trimmedBody = editingBody.trim();
    if (!trimmedBody) {
      setError([
        {
          message: formatMessage({
            id: 'COMMENT_IS_REQUIRED',
            defaultMessage: 'Comment is required',
          }),
        },
      ]);
      return;
    }
    setSaving(true);
    const result = await updateIssueComment({
      dataProvider,
      issueId,
      commentId: comment.id,
      body: trimmedBody,
      recordVersion: comment.recordVersion,
    });
    setSaving(false);
    if (result.ok) {
      setEditingId(null);
      setEditingBody('');
      await loadComments();
      onChanged();
      return;
    }
    setError(
      result?.errors || [
        {
          message: formatMessage({
            id: 'COULD_NOT_UPDATE_COMMENT',
            defaultMessage: 'Could not update comment',
          }),
        },
      ],
    );
  };

  return (
    <Box>
      {canManageComments && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<IconLibrary.Note />}
            onClick={() => setComposerOpen(open => !open)}>
            {formatMessage({
              id: 'ADD_COMMENT',
              defaultMessage: 'Add Comment',
            })}
          </Button>
        </Stack>
      )}

      <Box sx={{ p: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            <SnackbarApiErrorMessage errors={error} />
          </Alert>
        )}
        {canManageComments && composerOpen && (
          <Card variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack spacing={1}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={body}
                placeholder={formatMessage({
                  id: 'ADD_A_COMMENT',
                  defaultMessage: 'Add a comment',
                })}
                onChange={event => setBody(event.target.value)}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving || !body.trim()}
                  onClick={submitComment}>
                  {saving
                    ? formatMessage({
                        id: 'SAVING',
                        defaultMessage: 'Saving...',
                      })
                    : formatMessage({
                        id: 'ADD_COMMENT',
                        defaultMessage: 'Add Comment',
                      })}
                </Button>
                <Button
                  size="small"
                  disabled={saving}
                  onClick={() => {
                    setBody('');
                    setComposerOpen(false);
                  }}>
                  {formatMessage({
                    id: 'CANCEL',
                    defaultMessage: 'Cancel',
                  })}
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}

        {loading && (
          <Typography sx={{ m: 2 }}>
            {formatMessage({
              id: 'LOADING_COMMENTS',
              defaultMessage: 'Loading comments...',
            })}
          </Typography>
        )}
        {!loading && comments.length === 0 && (
          <Typography sx={{ m: 2 }}>
            {formatMessage({
              id: 'NO_COMMENTS_YET',
              defaultMessage: 'No comments yet',
            })}
          </Typography>
        )}
        <Stack spacing={2}>
          {comments.map(comment => (
            <IssueCommentCard
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              canManageComments={canManageComments}
              editingId={editingId}
              editingBody={editingBody}
              saving={saving}
              setEditingId={setEditingId}
              setEditingBody={setEditingBody}
              saveEdit={saveEdit}
              formatMessage={formatMessage}
            />
          ))}
        </Stack>
        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              size="small"
              onChange={(_event, nextPage) => setPage(nextPage)}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default IssueComments;
